import { catalogueDefaultPermissionsForRoleNames } from '@carii/casl-abilities';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { useAuth } from '../../context/AuthContext.js';
import { Button } from './Button.js';
import { Spinner } from './Spinner.js';

interface PrivateRouteProps {
  children: ReactNode;
}

function AccessDeniedPage() {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-ink mb-2">Access Denied</h2>
        <p className="text-steel mb-6">
          Your account does not have permission to access this panel.
        </p>
        <Button variant="secondary" onClick={logout}>
          Sign Out
        </Button>
      </div>
    </div>
  );
}

export function PrivateRoute({ children }: PrivateRouteProps) {
  const { loading, isAuthenticated, user, ability } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // 334 (#864, FR-002): admitted when the person holds at least one staff permission, never by a role's name. A
  // SuperAdmin's power is `manage all` (no list); everyone else's is the resolved set the platform publishes. From an
  // older backend without that field, the shared package's built-in defaults stand in, which admits exactly the
  // Admin, Supplier and Host that the old name list admitted. A customer holds no permission and stays out.
  const permissions =
    user?.permissions ??
    catalogueDefaultPermissionsForRoleNames(user?.roles.map((r) => r.name) ?? []);
  const isStaff = user !== null && (ability.can('manage', 'all') || permissions.length > 0);
  if (!isStaff) return <AccessDeniedPage />;

  return <>{children}</>;
}
